import {
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	TemplateRef,
	ViewChild,
	ViewContainerRef
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ProjectTimelineComponent } from '../project-timeline/project-timeline.component';
import { CommentService } from '../../../../services/comment.service';
import { Project } from '../../../../interfaces';
import { MessageSquareText } from 'lucide-angular';

@Component({
	selector: 'app-comment-preview',
	templateUrl: './comment-preview.component.html',
	styleUrls: ['./comment-preview.component.scss'],
})
export class CommentPreviewComponent implements OnInit, OnDestroy {
	@Input() public project: Project;
	@ViewChild('button') public button: ElementRef;
	@ViewChild('modalTemplate') public modalTemplate: TemplateRef<any>;
	@ViewChild('timeline') public timeline: ProjectTimelineComponent;

	public showDialog: boolean = false;
	private overlayRef?: OverlayRef;

	constructor(
		private overlay: Overlay,
		private viewContainerRef: ViewContainerRef,
		private commentService: CommentService
	) {}

	public ngOnInit() {
		this.commentService.activeDialog$.subscribe(activeDialog => {
			if (activeDialog !== this) {
				this.closeDialog();
			}
		});
	}

	public ngOnDestroy(): void {
		this.closeDialog();
	}

	public toggleModal(e: MouseEvent) {
		e.preventDefault();
		e.stopPropagation();

		if (!this.showDialog) {
			this.openModal();
		} else {
			this.closeDialog();
		}
	}

	private openModal(): void {
		this.commentService.setActiveDialog(this);

		const target = this.button.nativeElement;

		const positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(target)
			.withPositions([
				{
					originX: 'end',
					originY: 'bottom',
					overlayX: 'end',
					overlayY: 'top',
					offsetY: 8,
				},
				{
					originX: 'end',
					originY: 'top',
					overlayX: 'end',
					overlayY: 'bottom',
					offsetY: -8,
				},
				{
					originX: 'start',
					originY: 'bottom',
					overlayX: 'start',
					overlayY: 'top',
					offsetY: 8,
				}
			])
			.withPush(true);

		this.overlayRef = this.overlay.create({
			positionStrategy,
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
			hasBackdrop: true,
			backdropClass: 'cdk-overlay-transparent-backdrop',
			panelClass: 'comment-preview-overlay-pane',
		});

		const portal = new TemplatePortal(
			this.modalTemplate,
			this.viewContainerRef
		);

		this.overlayRef.attach(portal);
		this.showDialog = true;

		this.overlayRef.backdropClick().subscribe(() => this.closeDialog());

		this.overlayRef.keydownEvents().subscribe(event => {
			if (event.key === 'Escape') {
				this.closeDialog();
			}
		});
	}

	public closeDialog(): void {
		if (this.overlayRef) {
			this.overlayRef.detach();
			this.overlayRef.dispose();
			this.overlayRef = undefined;
		}
		this.showDialog = false;
	}

	protected readonly MessageSquareText = MessageSquareText;
}